import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_CALCULATION,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import type {
  BaseFormValues,
  ChartDynamicMapping,
  DashboardTemplate,
  TemplateChartDef,
  TemplateDateTermInput,
  TemplateFormConfig,
  WordCloudTemplateChartConfig,
} from '@/entities/dashboard-template';
import { ChartDetailPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import { getTimezoneDayjs } from '@/utils/time';

// --- 유틸리티 함수 ---

/**
 * fixed term 입력 → ChartFixedTermDef 변환
 * YYYY-MM-DD 문자열을 워크스페이스 타임존 기준 ISO 문자열로 변환
 */
const buildFixedTermDef = (fixedTerm: {
  startDate: string;
  endDate: string;
}): ChartFixedTermDef => ({
  type: CHART_TERM_TYPE.FIXED,
  unit: CHART_TERM_UNIT.DAY,
  start_date: getTimezoneDayjs(fixedTerm.startDate).toISOString(),
  end_date: getTimezoneDayjs(fixedTerm.endDate).toISOString(),
});

/**
 * 템플릿 dateTerm 입력을 ChartTermDef로 변환
 */
const buildTermDef = (
  dateTerm: TemplateDateTermInput | undefined,
): ChartTermDef => {
  if (!dateTerm) {
    return buildFixedTermDef({ startDate: '', endDate: '' });
  }

  if (dateTerm.type === CHART_TERM_TYPE.FIXED) {
    return buildFixedTermDef(dateTerm);
  }

  return {
    type: CHART_TERM_TYPE.PERIODIC,
    unit: dateTerm.unit,
    relative: dateTerm.relative,
    value: dateTerm.value,
  };
};

/**
 * 여러 StructuredFilter를 하나로 합치기
 * 동일 키가 있으면 뒤의 값이 우선
 */
const mergeFilters = (...filters: StructuredFilter[]): StructuredFilter => {
  const result: StructuredFilter = {};
  for (const filter of filters) {
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) {
        result[key] = value;
      }
    }
  }
  return result;
};

const matchesSectionVariant = <TFormValues extends BaseFormValues>(
  sectionVariants: string[] | undefined,
  formValues: TFormValues,
): boolean => {
  if (!sectionVariants || sectionVariants.length === 0) {
    return true;
  }

  const sectionVariant = (formValues as { sectionVariant?: string })
    .sectionVariant;

  return sectionVariant ? sectionVariants.includes(sectionVariant) : true;
};

const getPeriodLabel = (
  dateTerm: TemplateDateTermInput | undefined,
): string => {
  if (!dateTerm || dateTerm.type === CHART_TERM_TYPE.FIXED) {
    return 'Daily';
  }

  switch (dateTerm.unit) {
    case CHART_TERM_UNIT.WEEK:
      return 'Weekly';
    case CHART_TERM_UNIT.MONTH:
      return 'Monthly';
    case CHART_TERM_UNIT.QUARTER:
      return 'Last Quarter';
    case CHART_TERM_UNIT.DAY:
    default:
      return 'Daily';
  }
};

/**
 * {{key}} 토큰을 formValues의 값으로 치환
 */
export const resolveNameTemplate = <TFormValues extends BaseFormValues>(
  template: string,
  formValues: TFormValues,
): string => {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    if (key === 'periodLabel') {
      return getPeriodLabel(
        (formValues as { dateTerm?: TemplateDateTermInput }).dateTerm,
      );
    }

    const value = (formValues as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : String(value ?? '');
  });
};

// --- 매핑 resolve 함수 ---

/**
 * formConfig.fieldMapping을 사용하여 sourceField에 해당하는 필터를 빌드
 */
const buildFilterForSourceField = <TFormValues extends BaseFormValues>(
  sourceField: string,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): StructuredFilter => {
  const mapper = formConfig.fieldMapping[sourceField];
  return mapper ? mapper(formValues) : {};
};

/**
 * StructuredFilter에서 첫 번째 키의 StringNumberValue[] 값을 추출
 * 템플릿 매핑에서는 항상 StringNumberValue (product_id, topic_id 등) 사용
 */
const extractFilterValues = (filter: StructuredFilter): StringNumberValue[] | undefined => {
  const entry = Object.values(filter)[0];
  if (!Array.isArray(entry) || entry.length === 0) return undefined;
  const first = entry[0] as StructuredFilterStringNumberValueSet;
  return first.values;
};

/**
 * field.candidates / selected_field.items에는 string만 허용
 */
const toStringValues = (
  values: StringNumberValue[] | undefined,
): string[] | undefined => {
  if (!values) return undefined;
  return values.map(String);
};

/**
 * sourceField로부터 StringNumberValue[]를 resolve
 */
const resolveValuesFromMapping = <TFormValues extends BaseFormValues>(
  sourceField: string | undefined,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): string[] | undefined => {
  if (!sourceField) return undefined;
  const filter = buildFilterForSourceField(sourceField, formConfig, formValues);
  return toStringValues(extractFilterValues(filter));
};

/**
 * target 동적 조립: filter + term
 */
const buildTarget = <TFormValues extends BaseFormValues>(
  mapping: ChartDynamicMapping,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): ChartTargetDef => {
  // 필터 빌드
  const sourceFilters: StructuredFilter[] = (
    mapping.filterSourceFields ?? []
  ).map((field) => buildFilterForSourceField(field, formConfig, formValues));

  const filter = mergeFilters(...sourceFilters, mapping.extraFilter ?? {});

  // term 빌드
  const termSourceField = mapping.termSourceField ?? 'dateTerm';
  const dateTerm = (formValues as Record<string, unknown>)[
    termSourceField
  ] as TemplateDateTermInput | undefined;
  const term = buildTermDef(dateTerm);

  return {
    term,
    ...(Object.keys(filter).length > 0 && { filter }),
  };
};

// --- 메인 빌더 ---

/**
 * WordCloud 차트용 페이로드 빌드
 */
const buildWordCloudPayload = (
  name: string,
  target: ChartTargetDef,
  chart: WordCloudTemplateChartConfig,
): CreateChartPayload => ({
  name,
  type: CHART_TYPE.SINGLE_AXIS,
  target,
  graph: chart.graph,
  field: chart.field,
  graph_direction: chart.graph_direction,
  selected_field: {
    method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_100,
    items: [],
  },
  historical: chart.historical,
  series: [
    {
      measure: {
        metric: CHART_METRIC.FEEDBACK,
        calculation: CHART_CALCULATION.COUNT,
      },
      options: {
        view_type: CHART_METRIC_VIEW_TYPE.COUNT,
      },
    },
  ],
});

/**
 * General 차트용 페이로드 빌드 (ChartDetailPreprocess 파이프라인 활용)
 */
const buildGeneralChartPayload = <TFormValues extends BaseFormValues>(
  name: string,
  chartConfig: Omit<ChartBase, 'target'>,
  target: ChartTargetDef,
  mapping: ChartDynamicMapping,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): CreateChartPayload => {
  // 동적 candidates/items resolve
  const candidates = resolveValuesFromMapping(mapping.candidatesSourceField, formConfig, formValues);
  const items = resolveValuesFromMapping(mapping.itemsSourceField, formConfig, formValues);
  const crossCandidates = resolveValuesFromMapping(mapping.crossFieldCandidatesSourceField, formConfig, formValues);

  // field에 candidates 주입
  const field: ChartFieldDef = {
    ...chartConfig.field,
    ...(candidates && { candidates }),
  };

  // selected_field에 items 주입
  const selectedField: ChartSelectedFieldItemDef = {
    ...chartConfig.selected_field,
    ...(items && { items }),
  };

  // series에 cross_field.candidates 주입
  const series: ChartSeriesDef[] = chartConfig.series.map((s) => {
    if (s.cross_field && crossCandidates) {
      return {
        ...s,
        cross_field: {
          ...s.cross_field,
          candidates: crossCandidates,
        },
      };
    }
    return s;
  });

  // ChartBaseWithName 조립
  const chartBaseWithName: ChartBaseWithName = {
    name,
    type: chartConfig.type,
    historical: chartConfig.historical,
    graph: chartConfig.graph,
    field,
    selected_field: selectedField,
    target,
    series,
    ...(chartConfig.graph_direction !== undefined && {
      graph_direction: chartConfig.graph_direction,
    }),
  };

  // ChartDetailPreprocess로 최종 정리 (series options 클린업 등)
  return new ChartDetailPreprocess(chartBaseWithName).runPreprocess();
};

/**
 * 단일 TemplateChartDef → CreateChartPayload 변환
 */
const buildSingleChartPayload = <TFormValues extends BaseFormValues>(
  def: TemplateChartDef,
  formConfig: TemplateFormConfig<TFormValues>,
  formValues: TFormValues,
): CreateChartPayload => {
  const name = resolveNameTemplate(def.nameTemplate, formValues);
  const target = buildTarget(def.mapping, formConfig, formValues);

  if (def.chart.graph === CHART_GRAPH.WORD_CLOUD) {
    return buildWordCloudPayload(name, target, def.chart);
  }

  // GeneralTemplateChartConfig — Omit<ChartBase, 'target'>
  const chartConfig = def.chart as Omit<ChartBase, 'target'>;
  return buildGeneralChartPayload(
    name,
    chartConfig,
    target,
    def.mapping,
    formConfig,
    formValues,
  );
};

/**
 * 템플릿의 모든 차트를 CreateChartPayload[]로 변환
 */
export const buildChartPayloads = <TFormValues extends BaseFormValues>(
  template: DashboardTemplate<TFormValues>,
  formValues: TFormValues,
): CreateChartPayload[] => {
  return template.charts
    .filter((def) => matchesSectionVariant(def.sectionVariants, formValues))
    .map((def) =>
      buildSingleChartPayload(def, template.formConfig, formValues),
    );
};
